/**
 * Game action utilities and derived state computations.
 * Framework-agnostic — pure functions operating on GameState.
 */
// ─── Derived State ──────────────────────────────────────────────────
/**
 * Get the local player from game state.
 */
export function getLocalPlayer(state) {
    return state.players[state.localPlayerIndex] ?? null;
}
/**
 * Get the current turn player.
 */
export function getCurrentTurnPlayer(state) {
    return state.players[state.currentTurnIndex] ?? null;
}
/**
 * Get turn information for the UI.
 */
export function getTurnInfo(state) {
    const localPlayer = getLocalPlayer(state);
    const currentPlayer = getCurrentTurnPlayer(state);
    const timeRemainingPct = state.turnTotalMs > 0
        ? (state.turnTimeRemainingMs / state.turnTotalMs) * 100
        : 100;
    return {
        isMyTurn: localPlayer !== null && currentPlayer !== null && localPlayer.id === currentPlayer.id,
        currentPlayer,
        timeRemainingPct,
        isLowTime: state.turnTimeRemainingMs <= 10_000,
    };
}
/**
 * Get a summary of the game state for compact display.
 */
export function getGameSummary(state) {
    const localPlayer = getLocalPlayer(state);
    const leadingPlayer = state.players.length > 0
        ? state.players.reduce((best, p) => (p.totalScore > best.totalScore ? p : best))
        : null;
    return {
        roundNumber: state.roundNumber,
        localPlayer,
        localCall: localPlayer?.call ?? null,
        localTricksWon: localPlayer?.tricksWon ?? 0,
        localScore: localPlayer?.totalScore ?? 0,
        leadingPlayer,
    };
}
// ─── Card Playability ───────────────────────────────────────────────
/**
 * Check which cards in the hand are playable given the current trick state.
 * Follows Call Bridge rules: must follow lead suit if able, spades always trump.
 */
export function getPlayableCardInfos(hand, leadSuit, trickCards) {
    // If no lead suit, all cards are playable (we're leading)
    if (leadSuit === null || trickCards.length === 0) {
        return hand.map((card, index) => ({
            card,
            index,
            isPlayable: true,
            reason: null,
        }));
    }
    // Check if player has cards of the lead suit
    const hasLeadSuit = hand.some((c) => c.suit === leadSuit);
    return hand.map((card, index) => {
        if (hasLeadSuit) {
            // Must follow lead suit
            const isPlayable = card.suit === leadSuit;
            return {
                card,
                index,
                isPlayable,
                reason: isPlayable ? null : 'Must follow lead suit',
            };
        }
        // No lead suit cards — can play anything
        return { card, index, isPlayable: true, reason: null };
    });
}
/**
 * Whether the local player can currently play a card.
 */
export function canPlayCard(state) {
    if (state.phase !== 'playing')
        return false;
    const turnInfo = getTurnInfo(state);
    return turnInfo.isMyTurn && state.hand.length > 0;
}
/**
 * Whether a specific card at the given index is playable.
 */
export function isCardPlayable(state, cardIndex) {
    if (!canPlayCard(state))
        return false;
    const playable = getPlayableCardInfos(state.hand, state.leadSuit, state.trickCards);
    return playable[cardIndex]?.isPlayable ?? false;
}
// ─── Call Utilities ─────────────────────────────────────────────────
/**
 * Whether the local player can currently make a call.
 */
export function canMakeCall(state) {
    if (state.phase !== 'calling')
        return false;
    const turnInfo = getTurnInfo(state);
    const localPlayer = getLocalPlayer(state);
    return turnInfo.isMyTurn && localPlayer?.call === null;
}
/**
 * Get valid call values (2-13).
 */
export function getValidCallValues() {
    return Array.from({ length: 12 }, (_, i) => i + 2);
}
// ─── Player Ordering ────────────────────────────────────────────────
/**
 * Get players ordered relative to local player position.
 * Local player is always at bottom (index 0), then clockwise.
 */
export function getRelativePlayerOrder(state) {
    const count = state.players.length;
    if (count === 0)
        return [];
    const result = [];
    for (let i = 0; i < count; i++) {
        const idx = (state.localPlayerIndex + i) % count;
        const player = state.players[idx];
        if (player)
            result.push(player);
    }
    return result;
}
/**
 * Map a player to a seat position label for UI rendering.
 * Returns 'bottom', 'left', 'top', 'right' for 4-player games.
 */
export function getSeatPosition(relativeIndex, totalPlayers) {
    if (totalPlayers !== 4)
        return 'bottom';
    switch (relativeIndex) {
        case 0: return 'bottom';
        case 1: return 'left';
        case 2: return 'top';
        case 3: return 'right';
        default: return 'bottom';
    }
}
//# sourceMappingURL=useGame.js.map